import { endOfMonth, endOfWeek, startOfMonth, startOfWeek } from "date-fns";
import type { Appointment } from "@/features/appointments/types/appointmentTypes";
import { businessDayBounds } from "@/features/appointments/utils/bookingTime";
import { calendarDateKey } from "@/shared/utils/calendarDate";
import { getBusinessDateKey } from "@/shared/utils/formatters";
import type { CalendarEvent } from "../types/calendarEventTypes";

export type AgendaItem =
  | { kind: "appointment"; id: string; startAt: Date; appointment: Appointment }
  | { kind: "event"; id: string; startAt: Date; event: CalendarEvent }
  | { kind: "gap"; id: string; startAt: Date; endAt: Date };

export function visibleCalendarRange(month: Date, timezone: string) {
  return {
    startAt: businessDayBounds(calendarDateKey(startOfWeek(startOfMonth(month))), timezone).startAt,
    endAt: businessDayBounds(calendarDateKey(endOfWeek(endOfMonth(month))), timezone).endAt,
  };
}

export function eventTouchesDay(event: CalendarEvent, dateKey: string, timezone: string) {
  return !event.deletedAt && getBusinessDateKey(event.startAt, timezone) <= dateKey
    && getBusinessDateKey(new Date(event.endAt.getTime() - 1), timezone) >= dateKey;
}

export function eventDaySegment(event: CalendarEvent, dateKey: string, timezone: string) {
  const day = businessDayBounds(dateKey, timezone);
  return {
    startAt: new Date(Math.max(event.startAt.getTime(), day.startAt.getTime())),
    endAt: new Date(Math.min(event.endAt.getTime(), day.endAt.getTime())),
    continuesBefore: event.startAt < day.startAt,
    continuesAfter: event.endAt > day.endAt,
  };
}

export function createAgenda(
  appointments: Appointment[], events: CalendarEvent[], dateKey: string, timezone: string, showGaps: boolean,
): { allDay: CalendarEvent[]; timed: AgendaItem[] } {
  const dayEvents = events.filter((event) => eventTouchesDay(event, dateKey, timezone));
  const sorted = [...appointments].sort((a, b) => (a.startAt?.getTime() ?? Infinity) - (b.startAt?.getTime() ?? Infinity));
  const timed: AgendaItem[] = sorted.map((appointment) => ({
    kind: "appointment", id: `appointment-${appointment.id}`, startAt: appointment.startAt ?? new Date(8.64e15), appointment,
  }));
  for (const event of dayEvents.filter((item) => !item.isAllDay)) {
    timed.push({ kind: "event", id: `event-${event.id}`, startAt: eventDaySegment(event, dateKey, timezone).startAt, event });
  }
  const busy = dayEvents.filter((event) => event.blocksAvailability).sort((a, b) => a.startAt.getTime() - b.startAt.getTime());
  let occupiedUntil = 0;
  if (showGaps) sorted.forEach((appointment, index) => {
    const next = sorted[index + 1];
    if (!["pending", "confirmed"].includes(appointment.status)) return;
    occupiedUntil = Math.max(occupiedUntil, appointment.endAt?.getTime() ?? 0);
    if (!appointment.endAt || !next?.startAt || !["pending", "confirmed"].includes(next.status)) return;
    const end = next.startAt.getTime();
    let cursor = occupiedUntil;
    const addGap = (start: number, finish: number) => {
      if (finish - start < 60_000) return;
      timed.push({ kind: "gap", id: `gap-${start}-${finish}`, startAt: new Date(start), endAt: new Date(finish) });
    };
    for (const event of busy) {
      if (event.endAt.getTime() <= cursor || event.startAt.getTime() >= end) continue;
      addGap(cursor, Math.min(end, event.startAt.getTime()));
      cursor = Math.max(cursor, event.endAt.getTime());
      if (cursor >= end) break;
    }
    addGap(cursor, end);
  });
  return {
    allDay: dayEvents.filter((event) => event.isAllDay).sort((a, b) => a.startAt.getTime() - b.startAt.getTime()),
    timed: timed.sort((a, b) => a.startAt.getTime() - b.startAt.getTime()),
  };
}
